import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { authApi } from "../../../auth/api.ts";
import { useAuthStore } from "../../../auth/store.ts";

const STORAGE_KEY = "sally.live.layout.v2";

// Rail default is wide enough for the company-intel panel (Research/Billing/Growth
// tabs + charts) that now lives there under My notes; still resizable.
const DEFAULTS = { rail: 360, coach: 460, transcript: 400 } as const;
const MIN = { rail: 280, coach: 320, transcript: 280 } as const;
// Rail (screen-share section) can be dragged much wider so users can make the
// shared screen large.
const MAX = { rail: 1400, coach: 900, transcript: 800 } as const;

interface Widths {
  rail: number;
  coach: number;
  transcript: number;
}

interface ResizableMainProps {
  rail: ReactNode;
  coach: ReactNode;
  transcript: ReactNode;
}

function clampWidths(parsed: Partial<Widths>): Widths {
  return {
    rail: Math.max(MIN.rail, Math.min(parsed.rail ?? DEFAULTS.rail, MAX.rail)),
    coach: Math.max(MIN.coach, Math.min(parsed.coach ?? DEFAULTS.coach, MAX.coach)),
    transcript: Math.max(
      MIN.transcript,
      Math.min(parsed.transcript ?? DEFAULTS.transcript, MAX.transcript),
    ),
  };
}

function loadWidths(): Widths {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return clampWidths(JSON.parse(raw) as Partial<Widths>);
  } catch {}
  const saved = useAuthStore.getState().user?.settings.layoutWidths;
  if (saved) return clampWidths(saved);
  return { ...DEFAULTS };
}

function saveWidths(w: Widths): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(w));
  } catch {}
}

/**
 * Three-column live-meeting layout with drag-resizable boundaries.
 * Persists to localStorage so per-rep preferences stick across sessions.
 */
export function ResizableMain({ rail, coach, transcript }: ResizableMainProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<Widths>(() =>
    typeof window === "undefined" ? { ...DEFAULTS } : loadWidths(),
  );
  const dragRef = useRef<{
    which: "rail-coach" | "coach-transcript";
    startX: number;
    startWidths: Widths;
  } | null>(null);

  useEffect(() => {
    saveWidths(widths);
  }, [widths]);

  useEffect(() => {
    return () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const current = JSON.parse(raw) as Widths;
          authApi.updateSettings({ layoutWidths: current }).catch(() => {});
        }
      } catch {}
    };
  }, []);

  const onMouseDown = useCallback(
    (which: "rail-coach" | "coach-transcript") => (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { which, startX: e.clientX, startWidths: { ...widths } };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widths],
  );

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (drag.which === "rail-coach") {
        const nextRail = Math.max(MIN.rail, Math.min(drag.startWidths.rail + dx, MAX.rail));
        setWidths((w) => ({ ...w, rail: nextRail }));
      } else {
        const nextCoach = Math.max(MIN.coach, Math.min(drag.startWidths.coach + dx, MAX.coach));
        const nextTranscript = Math.max(
          MIN.transcript,
          Math.min(drag.startWidths.transcript - dx, MAX.transcript),
        );
        setWidths((w) => ({ ...w, coach: nextCoach, transcript: nextTranscript }));
      }
    }
    function onUp() {
      if (dragRef.current) {
        dragRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const gridStyle = useMemo<React.CSSProperties>(
    () => ({
      gridTemplateColumns: `${widths.rail}px 6px minmax(0, 1fr) 6px ${widths.transcript}px`,
    }),
    [widths.rail, widths.transcript],
  );

  return (
    <main className="main main-resizable" ref={containerRef} style={gridStyle}>
      <div className="resizable-col rail-col">{rail}</div>
      <div
        className="col-resizer"
        onMouseDown={onMouseDown("rail-coach")}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize rail / coach"
        title="Drag to resize"
      />
      <div className="resizable-col coach-col">{coach}</div>
      <div
        className="col-resizer"
        onMouseDown={onMouseDown("coach-transcript")}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize coach / transcript"
        title="Drag to resize"
      />
      <div className="resizable-col transcript-col">{transcript}</div>
    </main>
  );
}

export function resetLiveLayout(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  authApi.updateSettings({ layoutWidths: undefined }).catch(() => {});
  window.location.reload();
}
